import React, { useState, useEffect } from 'react';
import { conversionCategories } from '../../data/conversionData';

export const UnitConverter: React.FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<string>('cooling');
    const [inputValue, setInputValue] = useState<string>('1');
    const [fromUnit, setFromUnit] = useState<string>('');
    const [toUnit, setToUnit] = useState<string>('');
    const [result, setResult] = useState<number | null>(null);

    const category = conversionCategories.find((c) => c.id === selectedCategory);

    useEffect(() => {
        if (category) {
            setFromUnit(category.units[0].id);
            setToUnit(category.units[1]?.id || category.units[0].id);
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (!category || !fromUnit || !toUnit || inputValue === '') {
            setResult(null);
            return;
        }

        const value = parseFloat(inputValue);
        if (isNaN(value)) {
            setResult(null);
            return;
        }

        const converted = category.convert(value, fromUnit, toUnit);
        setResult(converted);
    }, [inputValue, fromUnit, toUnit, category]);

    const handleSwap = () => {
        setFromUnit(toUnit);
        setToUnit(fromUnit);
    };

    const formatNumber = (num: number): string => {
        if (Math.abs(num) < 0.001 || Math.abs(num) >= 1000000) {
            return num.toExponential(4);
        }
        return num.toLocaleString('ko-KR', { maximumFractionDigits: 4 });
    };

    return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {conversionCategories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        style={{
                            padding: '16px',
                            borderRadius: '10px',
                            border: selectedCategory === cat.id ? '2px solid #1e3a5f' : '2px solid #c4b8a5',
                            backgroundColor: selectedCategory === cat.id ? '#e3f2fd' : 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        <span style={{ fontSize: '24px' }}>{cat.icon}</span>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: selectedCategory === cat.id ? 700 : 400,
                            color: selectedCategory === cat.id ? '#1e3a5f' : '#5a5a5a'
                        }}>
                            {cat.name}
                        </span>
                    </button>
                ))}
            </div>

            {category && (
                <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: '#1e3a5f' }}>
                        {category.icon} {category.name} 변환
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                            <label style={{ fontSize: '12px', color: '#5a5a5a', marginBottom: '4px', display: 'block', fontWeight: 700 }}>값</label>
                            <input
                                type="number"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #c4b8a5',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontFamily: "'Roboto Mono', monospace",
                                    outline: 'none'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '12px', color: '#5a5a5a', marginBottom: '4px', display: 'block', fontWeight: 700 }}>From</label>
                                <select
                                    value={fromUnit}
                                    onChange={(e) => setFromUnit(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #c4b8a5',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontFamily: "'Noto Sans KR', sans-serif",
                                        outline: 'none',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    {category.units.map((unit) => (
                                        <option key={unit.id} value={unit.id}>
                                            {unit.name} ({unit.symbol})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleSwap}
                                style={{
                                    padding: '8px',
                                    backgroundColor: '#f4f1ea',
                                    border: '1px solid #c4b8a5',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    marginTop: '20px'
                                }}
                            >
                                ⇄
                            </button>

                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '12px', color: '#5a5a5a', marginBottom: '4px', display: 'block', fontWeight: 700 }}>To</label>
                                <select
                                    value={toUnit}
                                    onChange={(e) => setToUnit(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #c4b8a5',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontFamily: "'Noto Sans KR', sans-serif",
                                        outline: 'none',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    {category.units.map((unit) => (
                                        <option key={unit.id} value={unit.id}>
                                            {unit.name} ({unit.symbol})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{
                            marginTop: '8px',
                            padding: '16px',
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #1e3a5f',
                            textAlign: 'center'
                        }}>
                            <p style={{ fontSize: '14px', color: '#5a5a5a', marginBottom: '4px' }}>
                                {inputValue} {category.units.find(u => u.id === fromUnit)?.symbol} =
                            </p>
                            <p style={{ fontSize: '28px', fontWeight: 700, color: '#1e3a5f', fontFamily: "'Roboto Mono', monospace" }}>
                                {result !== null ? formatNumber(result) : '-'}
                            </p>
                            <p style={{ fontSize: '16px', color: '#1e3a5f', fontWeight: 700 }}>
                                {category.units.find(u => u.id === toUnit)?.symbol}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: '#1e3a5f' }}>
                    📋 빠른 참고
                </h3>
                <div style={{ fontSize: '13px', color: '#4b5563', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', backgroundColor: '#f4f1ea', border: '1px solid #e8e0d5' }}>
                        <span>1 RT (냉동톤)</span>
                        <span style={{ fontWeight: 700, color: '#1e3a5f', fontFamily: "'Roboto Mono', monospace" }}>= 3.517 kW</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', backgroundColor: '#f4f1ea', border: '1px solid #e8e0d5' }}>
                        <span>1 HP (마력)</span>
                        <span style={{ fontWeight: 700, color: '#1e3a5f', fontFamily: "'Roboto Mono', monospace" }}>= 0.746 kW</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', backgroundColor: '#f4f1ea', border: '1px solid #e8e0d5' }}>
                        <span>1 psi</span>
                        <span style={{ fontWeight: 700, color: '#1e3a5f', fontFamily: "'Roboto Mono', monospace" }}>= 0.069 bar</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', backgroundColor: '#f4f1ea', border: '1px solid #e8e0d5' }}>
                        <span>1 inch (관경)</span>
                        <span style={{ fontWeight: 700, color: '#1e3a5f', fontFamily: "'Roboto Mono', monospace" }}>= 25.4 mm</span>
                    </div>
                </div>
            </div>
        </div>
    );
};